import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { Link } from "react-router-dom";
import { ShieldStarIcon as ShieldStar, CopyIcon as Copy, CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, WarningIcon as Warning, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Admin guidance page for Stripe Radar fraud rules.
 *
 * Stripe Radar rules are configured in the Stripe Dashboard, NOT via
 * the API (Stripe enforces this for security). This page therefore
 * gives admins a copy-paste set of rule strings that match the
 * platform's known fraud surface:
 *
 *   • Escrow checkouts where buyer + seller email differ but card
 *     country matches a known carding hotspot
 *   • Repeat checkout attempts from the same IP across multiple
 *     buyers
 *   • High-value escrow (>$3000) without 3DS
 *   • Card from a country very different from buyer's claimed
 *     billing address country
 *
 * Each rule is presented as an exact Radar-rule expression that the
 * admin pastes into Stripe Dashboard → Radar → Rules → "Block if".
 */
const RULES = [
  {
    id: "block_high_value_no_3ds",
    title: "Block high-value escrow without 3D Secure",
    rationale:
      "Escrow holds buyer money on file. >$3,000 transactions in this category are statistically the riskiest — forcing 3DS limits chargeback exposure.",
    severity: "P0",
    expression: ":amount_in_usd: > 3000 AND :card_supports_3ds: = 'optional' AND :is_3d_secure_authenticated: = false",
  },
  {
    id: "block_card_country_mismatch",
    title: "Block when card country and IP country diverge",
    rationale:
      "Standard card-not-present fraud signal. A US-IP buyer paying with a card issued in a known carding country (e.g. RU/CN/NG) at this dollar range warrants a hard block.",
    severity: "P0",
    expression:
      ":card_country: != :ip_country: AND :amount_in_usd: > 500 AND :card_country: in ('NG','RU','UA','VN','CN','PK','BD')",
  },
  {
    id: "review_velocity_same_ip",
    title: "Review when 3+ checkouts from the same IP in 1 hour",
    rationale:
      "Carding scripts typically test stolen cards in rapid succession against the same checkout. A human breeder won't burn 3 cards on the same IP in an hour.",
    severity: "P1",
    expression: ":ip_address: matches ':ip_address:' AND :ip_address_count_for_card_country: > 2",
  },
  {
    id: "review_disposable_email",
    title: "Review checkouts from disposable-email domains",
    rationale:
      "Disposable email domains (mailinator, guerrillamail, tempmail, 10minutemail, etc.) correlate with chargebacks at ~12x the baseline rate.",
    severity: "P1",
    expression:
      ":email_domain: in ('mailinator.com','guerrillamail.com','tempmail.com','10minutemail.com','yopmail.com','sharklasers.com','dispostable.com','trashmail.com')",
  },
  {
    id: "block_card_tested_recently",
    title: "Block cards that failed in the last 7 days on this platform",
    rationale:
      "If a card has been declined or flagged on this very Stripe account in the last week, a successful charge today is almost always carding.",
    severity: "P0",
    expression:
      ":card_count_for_block_charges_in_last_7_days: >= 1",
  },
  {
    id: "review_high_velocity_email",
    title: "Review checkouts from emails with 4+ payments in 24h",
    rationale:
      "An email registering 4+ Stripe payments in 24h almost certainly isn't a hobbyist breeder buying a single dog.",
    severity: "P1",
    expression:
      ":email_count_in_last_24_hours: > 3 AND :amount_in_usd: > 250",
  },
];

const REVIEW_CHECKLIST = [
  "Stripe Dashboard → Settings → Radar → enable ML risk scoring",
  "Radar → Rules → block all rules with severity P0 above",
  "Radar → Rules → review (manual) all P1 rules above",
  "Settings → Payment methods → require 3DS for all escrow > $3000",
  "Settings → Payouts → switch to 7-day rolling reserve while in soft-launch",
  "Subscribe to Stripe's monthly fraud digest email",
  "Review every disputed escrow within 48h",
];

export default function AdminStripeRadar() {
  const [copied, setCopied] = useState(null);

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      toast.success("Copied — paste into Stripe Radar Rules");
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-2 md:px-0" data-testid="admin-stripe-radar">
        <Link to="/admin" className="text-[12px] text-[#6B6B6B] hover:text-ink inline-flex items-center gap-1 mb-4">
          <ArrowRight size={11} className="rotate-180" /> Back to admin console
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <ShieldStar size={28} weight="duotone" className="text-crimson" />
          <h1 className="font-spectral text-4xl tracking-tight">Stripe Radar — fraud rules</h1>
        </div>
        <p className="text-[14px] text-[#6B6B6B] max-w-2xl mb-8 leading-relaxed">
          Radar rules are configured in the <strong>Stripe Dashboard</strong>,
          not via API (Stripe enforces this for security). Below are 6 rules
          calibrated to this platform's escrow + breeding-listing checkout
          flows. Copy each expression and paste it into{" "}
          <a
            href="https://dashboard.stripe.com/test/radar/rules"
            target="_blank"
            rel="noreferrer"
            className="text-crimson font-bold inline-flex items-center gap-1 hover:underline"
            data-testid="stripe-radar-link"
          >
            Stripe → Radar → Rules <ArrowSquareOut size={11} />
          </a>
        </p>

        <div className="rounded-xl border border-[#FFE2A8] bg-[#FFF8E5] p-4 mb-6 text-[13px] text-[#8A6A00] flex gap-3" data-testid="radar-warning">
          <Warning size={18} weight="fill" className="flex-shrink-0 mt-[2px]" />
          <div>
            <p className="font-bold">Test in test-mode first.</p>
            <p>
              Always enable a rule in test-mode for 24h before flipping it
              live. Run a $1 escrow charge against the rule's example
              conditions and confirm the expected block / review behaviour.
            </p>
          </div>
        </div>

        <div className="space-y-4 mb-10">
          {RULES.map((r) => (
            <article
              key={r.id}
              className="rounded-xl border border-[#E5E5E5] bg-white p-5"
              data-testid={`radar-rule-${r.id}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <p className="font-bold text-ink">{r.title}</p>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded ${
                      r.severity === "P0"
                        ? "bg-[#FDEEEE] text-[#A12125] border border-[#EFA1A3]"
                        : "bg-[#FFF8E5] text-[#8A6A00] border border-[#FFD37D]"
                    }`}
                  >
                    {r.severity} — {r.severity === "P0" ? "Block" : "Review"}
                  </span>
                  <button
                    type="button"
                    onClick={() => copy(r.expression, r.id)}
                    className="text-[12px] text-crimson font-bold inline-flex items-center gap-1 hover:text-ink"
                    data-testid={`radar-rule-copy-${r.id}`}
                  >
                    {copied === r.id ? (
                      <><CheckCircle size={12} weight="fill" /> Copied</>
                    ) : (
                      <><Copy size={12} weight="bold" /> Copy expression</>
                    )}
                  </button>
                </div>
              </div>
              <p className="text-[13px] text-[#6B6B6B] mb-3">{r.rationale}</p>
              <pre className="bg-[#FAFAFA] border border-[#E5E5E5] rounded p-3 text-[12px] text-ink font-mono overflow-x-auto">
{r.expression}
              </pre>
            </article>
          ))}
        </div>

        <div className="rounded-xl border border-[#E5E5E5] bg-[#0E0E0E] text-white p-6 mb-10">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-gold mb-3">
            Beyond Radar — broader posture
          </p>
          <h2 className="font-spectral text-2xl tracking-tight mb-4">
            Once Radar is on, walk this checklist.
          </h2>
          <ol className="space-y-2 text-[13.5px] text-white/90 list-decimal list-inside">
            {REVIEW_CHECKLIST.map((c, i) => (
              <li key={i} data-testid={`radar-checklist-${i}`}>{c}</li>
            ))}
          </ol>
        </div>
      </div>
    </AppShell>
  );
}
